import React, { useState } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import moment from "moment";
import { toast } from "react-toastify";

export interface ExportColumn {
  header: string;
  key: string;
  type?: "currency" | "date" | "number" | "text";
  isAmount?: boolean;
  width?: number;
}

export interface ExportExcelOptions {
  fileName?: string;
  sheetName?: string;
  columns: ExportColumn[];
  data: any[];
}

export const downloadExcelFile = async ({
  fileName = "export",
  sheetName = "Sheet1",
  columns,
  data,
}: ExportExcelOptions): Promise<boolean> => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    toast.info("No data to export");
    return false;
  }

  // Create workbook and sheet
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "ReadyShare Admin";
  workbook.created = new Date();

  const worksheet = workbook.addWorksheet(sheetName, {
    views: [{ state: "frozen", ySplit: 1 }],
  });

  // Define worksheet columns
  worksheet.columns = columns.map((col) => ({
    header: col.header,
    key: col.key,
    width: col.width || Math.max(col.header.length + 4, 15),
  }));

  // Style Header Row - Inspinia Brand Green
  const headerRow = worksheet.getRow(1);
  headerRow.height = 26;
  headerRow.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };
  headerRow.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF1AB394" },
  };
  headerRow.alignment = { vertical: "middle", horizontal: "center", wrapText: true };

  // Accumulator for Totals Row
  const totalsAccumulator: { [key: string]: number } = {};
  columns.forEach((col) => {
    if (col.isAmount) {
      totalsAccumulator[col.key] = 0;
    }
  });

  // Add Data Rows
  data.forEach((item, rowIndex) => {
    const rowData: { [key: string]: any } = {};

    columns.forEach((col) => {
      let value = item[col.key];

      if (col.type === "date") {
        if (value) {
          const m = isNaN(Number(value)) ? moment(value) : moment(Number(value));
          value = m.isValid() ? m.format("DD MMM YYYY HH:mm") : value;
        } else {
          value = "N/A";
        }
      } else if (col.type === "currency" || col.type === "number") {
        const num = Number(value);
        value = isNaN(num) ? 0 : Math.round(num * 100) / 100;
        if (col.isAmount) {
          totalsAccumulator[col.key] += value;
        }
      } else {
        value = value !== undefined && value !== null ? String(value) : "";
      }

      rowData[col.key] = value;
    });

    const addedRow = worksheet.addRow(rowData);
    addedRow.height = 20;
    addedRow.alignment = { vertical: "middle" };

    // Zebra striping
    if (rowIndex % 2 === 1) {
      addedRow.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFF9FBFB" },
      };
    }

    // Number format and cell borders
    columns.forEach((col, idx) => {
      const cell = addedRow.getCell(idx + 1);
      cell.border = {
        top: { style: "thin", color: { argb: "FFE5E7EB" } },
        bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
        left: { style: "thin", color: { argb: "FFE5E7EB" } },
        right: { style: "thin", color: { argb: "FFE5E7EB" } },
      };

      if (col.type === "currency") {
        cell.numFmt = "#,##0.00";
        cell.alignment = { vertical: "middle", horizontal: "right" };
      } else if (col.type === "number") {
        cell.numFmt = "#,##0";
        cell.alignment = { vertical: "middle", horizontal: "right" };
      }
    });
  });

  // Add Totals Row if any column is marked as amount
  const hasAmountCol = columns.some((c) => c.isAmount);
  if (hasAmountCol) {
    const totalRowData: { [key: string]: any } = {};
    let firstColKey = columns[0]?.key;
    if (firstColKey) {
      totalRowData[firstColKey] = "TOTAL";
    }

    columns.forEach((col) => {
      if (col.isAmount) {
        totalRowData[col.key] = Math.round(totalsAccumulator[col.key] * 100) / 100;
      } else if (col.key !== firstColKey) {
        totalRowData[col.key] = "";
      }
    });

    const totalRow = worksheet.addRow(totalRowData);
    totalRow.height = 24;
    totalRow.font = { bold: true, size: 11, color: { argb: "FF2F4050" } };
    totalRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFE6F7F3" },
    };
    totalRow.alignment = { vertical: "middle" };

    totalRow.border = {
      top: { style: "thin", color: { argb: "FF1AB394" } },
      bottom: { style: "double", color: { argb: "FF1AB394" } },
      left: { style: "thin", color: { argb: "FFE5E7EB" } },
      right: { style: "thin", color: { argb: "FFE5E7EB" } },
    };

    columns.forEach((col, idx) => {
      if (col.isAmount) {
        const cell = totalRow.getCell(idx + 1);
        cell.numFmt = "#,##0.00";
        cell.alignment = { vertical: "middle", horizontal: "right" };
      }
    });
  }

  // Auto-fit column widths
  worksheet.columns.forEach((column: any) => {
    let maxLen = 0;
    column.eachCell({ includeEmpty: true }, (cell: any) => {
      const cellStr = cell.value ? cell.value.toString() : "";
      if (cellStr.length > maxLen) {
        maxLen = cellStr.length;
      }
    });
    column.width = Math.max(maxLen + 4, 14);
  });

  // Write to buffer and trigger download
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const formattedDate = moment().format("YYYY-MM-DD");
  const exportFileName = `${fileName}_${formattedDate}.xlsx`;

  saveAs(blob, exportFileName);
  toast.success(`File downloaded: ${exportFileName}`);
  return true;
};

interface ExportButtonProps {
  fileName?: string;
  sheetName?: string;
  columns: ExportColumn[];
  onFetchData: () => Promise<any[]>;
  buttonText?: string;
  className?: string;
  disabled?: boolean;
}

export const ExportButton: React.FC<ExportButtonProps> = ({
  fileName = "export",
  sheetName = "Sheet1",
  columns,
  onFetchData,
  buttonText = "Export",
  className = "btn btn-outline-success btn-sm",
  disabled = false,
}) => {
  const [loading, setLoading] = useState<boolean>(false);

  const handleExport = async () => {
    if (loading || disabled) return;

    try {
      setLoading(true);
      const rawData = await onFetchData();
      await downloadExcelFile({
        fileName,
        sheetName,
        columns,
        data: rawData,
      });
    } catch (error: any) {
      console.error("Export failed:", error);
      toast.error(error?.message || "Failed to export data to Excel");
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      className={`${className} d-inline-flex align-items-center`}
      onClick={handleExport}
      disabled={loading || disabled}
      title="Export to Excel (.xlsx)"
    >
      {loading ? (
        <>
          <i className="fa fa-spinner fa-spin me-1"></i>
          <span>Exporting...</span>
        </>
      ) : (
        <>
          <i className="fas fa-file-excel text-success me-1"></i>
          <span>{buttonText}</span>
        </>
      )}
    </button>
  );
};

export default ExportButton;
